import keyBy from 'lodash/keyBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import { db } from 'src/utils/firebase';
import { setReactionsFor } from 'src/utils/chat-reactions';
import { subscribeToPresence } from 'src/utils/presence';
import { collection, query, where, getDocs, orderBy, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { FIREBASE_PREFIX } from 'src/config-global';

// Store for active listeners
let activeMessageListener = null;
let activeChatListListener = null;
let activeChatPresenceListener = null;

// Helper function to sort allIds by lastMessageTime
const sortConversationIds = (state) => {
  state.conversations.allIds = Object.keys(state.conversations.byId).sort((a, b) => {
    const convA = state.conversations.byId[a];
    const convB = state.conversations.byId[b];
    const timeA = convA?.lastMessageTime ? new Date(convA.lastMessageTime).getTime() : 0;
    const timeB = convB?.lastMessageTime ? new Date(convB.lastMessageTime).getTime() : 0;
    return timeB - timeA; // Descending order (newest first)
  });
};

// Firestore returns nested fields as `Timestamp` instances which are not
// serializable. Convert any timestamp-like values inside a lastMessage
// payload to plain millisecond numbers before storing in Redux.
const sanitizeLastMessage = (lm) => {
  if (!lm || typeof lm !== 'object') return lm || null;
  const out = {};
  Object.entries(lm).forEach(([k, v]) => {
    if (v && typeof v === 'object' && typeof v.toMillis === 'function') {
      out[k] = v.toMillis();
    } else {
      out[k] = v;
    }
  });
  return out;
};
// ----------------------------------------------------------------------

const initialState = {
  contacts: [],
  allUsers: [], // All registered users for search
  recipients: [],
  currentConversationId: null,
  currentConversationLoading: false,
  conversations: { byId: {}, allIds: [] },
  conversationsStatus: {
    loading: false,
    empty: false,
    error: null,
  },
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // GET CONTACT
    getContactsSuccess(state, action) {
      state.contacts = action.payload;
    },

    // SEARCH ALL USERS
    searchUsersSuccess(state, action) {
      // This will be used for searching all registered users
      state.allUsers = action.payload;
    },

    // GET CONVERSATIONS
    getConversationsStart(state) {
      state.conversationsStatus.loading = true;
      state.conversationsStatus.empty = false;
      state.conversationsStatus.error = null;
    },
    getConversationsFailure(state, action) {
      state.conversationsStatus.loading = false;
      state.conversationsStatus.empty = false;
      state.conversationsStatus.error = action.payload;
    },
    getConversationsSuccess(state, action) {
      const conversations = action.payload;

      state.conversationsStatus.loading = false;
      state.conversationsStatus.empty = !conversations.length;
      state.conversationsStatus.error = null;

      // Merge new data with existing — preserve messages/details already loaded by getConversation
      const newById = keyBy(conversations, 'id');
      Object.keys(newById).forEach(id => {
        const existing = state.conversations.byId[id];
        if (existing && existing.messages && existing.messages.length > 0) {
          // Keep loaded messages and chatId, preserve the more recent lastMessageTime
          const existingTime = existing.lastMessageTime || 0;
          const newTime = newById[id].lastMessageTime || 0;
          const carriedLastMessage = existingTime > newTime
            ? (existing.lastMessage || newById[id].lastMessage)
            : newById[id].lastMessage;
          newById[id] = {
            ...newById[id],
            messages: existing.messages,
            chatId: existing.chatId || newById[id].chatId,
            lastMessageTime: Math.max(existingTime, newTime),
            lastMessage: sanitizeLastMessage(carriedLastMessage),
          };
        } else {
          newById[id] = {
            ...newById[id],
            lastMessage: sanitizeLastMessage(newById[id].lastMessage),
          };
        }
      });
      // Also keep conversations that exist in store but aren't in the API response
      // (e.g., new conversations started via getConversation that haven't been synced yet)
      // Sanitize legacy `lastMessage` values written before the Timestamp fix
      // landed so middleware no longer flags them.
      Object.keys(state.conversations.byId).forEach(id => {
        if (!newById[id]) {
          const carry = state.conversations.byId[id];
          newById[id] = {
            ...carry,
            lastMessage: sanitizeLastMessage(carry.lastMessage),
          };
        }
      });
      state.conversations.byId = newById;

      // Sort allIds by lastMessageTime in descending order (newest first)
      sortConversationIds(state);
    },

    // GET CONVERSATION (loading flag drives the message-panel skeleton)
    getConversationStart(state) {
      state.currentConversationLoading = true;
    },
    getConversationFailure(state) {
      state.currentConversationLoading = false;
    },
    getConversationSuccess(state, action) {
      state.currentConversationLoading = false;
      const conversation = action.payload;

      if (conversation && conversation.id) {
        state.recipients = [];
        // Do NOT set currentConversationId here — it is owned by setActiveConversation
        // (dispatched synchronously on user click). Setting it here caused a race: a
        // slow response for a previously-selected chat could overwrite the user's
        // current selection after they had already switched to a different chat.

        // Preserve lastMessageTime from existing conversation if not provided in new data
        const existingConversation = state.conversations.byId[conversation.id];
        if (existingConversation && !conversation.lastMessageTime) {
          conversation.lastMessageTime = existingConversation.lastMessageTime;
          conversation.lastMessage = existingConversation.lastMessage || conversation.lastMessage;
        }
        conversation.lastMessage = sanitizeLastMessage(conversation.lastMessage);

        state.conversations.byId[conversation.id] = conversation;
        if (!state.conversations.allIds.includes(conversation.id)) {
          state.conversations.allIds.push(conversation.id);
        }
        // console.log('getConversationSuccess: Set currentConversationId to', conversation.id);
        // console.log('getConversationSuccess: Stored conversation with key', conversation.id);
      } else {
        console.warn('Invalid conversation payload:', conversation);
        state.currentConversationId = null;
      }
    },

    // ON SEND MESSAGE
    sendMessageSuccess(state, action) {
      const { conversationId, message } = action.payload;

      // console.log('sendMessageSuccess: conversationId =', conversationId, 'type:', typeof conversationId);
      // console.log('sendMessageSuccess: available conversations =', Object.keys(state.conversations.byId));

      // Try to find the conversation with different type formats
      const conversation = state.conversations.byId[conversationId] ||
                          state.conversations.byId[String(conversationId)] ||
                          state.conversations.byId[Number(conversationId)];

      if (conversationId && conversation) {
        // Use the actual key that was found
        const actualKey = state.conversations.byId[conversationId] ? conversationId :
                         state.conversations.byId[String(conversationId)] ? String(conversationId) :
                         Number(conversationId);

        // Check if message already exists to avoid duplicates
        const existingMessage = state.conversations.byId[actualKey].messages.find(
          m => m.id === message.id
        );
        if (!existingMessage) {
          state.conversations.byId[actualKey].messages.push(message);
          // console.log('sendMessageSuccess: Message added to conversation with key:', actualKey);

          // Update lastMessageTime to current time for sorting
          state.conversations.byId[actualKey].lastMessageTime = Date.now();
          state.conversations.byId[actualKey].lastMessage = {
            content: message.body,
            type: message.contentType,
            senderId: message.senderId,
            timestamp: Date.now()
          };

          // Re-sort conversations by lastMessageTime
          sortConversationIds(state);
        } else {
          // console.log('sendMessageSuccess: Message already exists, skipping');
        }
      } else {
        // console.log('sendMessageSuccess: Conversation not found in store for id:', conversationId);
      }
    },

    // UPDATE MESSAGES IN REAL-TIME
    updateMessagesRealtime(state, action) {
      const { conversationId, messages } = action.payload;

      if (conversationId && state.conversations.byId[conversationId]) {
        const oldMessagesCount = state.conversations.byId[conversationId].messages.length;
        state.conversations.byId[conversationId].messages = messages;

        // Update lastMessageTime if there are new messages
        if (messages.length > 0 && messages.length !== oldMessagesCount) {
          const lastMsg = messages[messages.length - 1];
          state.conversations.byId[conversationId].lastMessageTime =
            lastMsg.createdAt ? new Date(lastMsg.createdAt).getTime() : Date.now();
          state.conversations.byId[conversationId].lastMessage = {
            content: lastMsg.body,
            type: lastMsg.contentType,
            senderId: lastMsg.senderId,
            timestamp: state.conversations.byId[conversationId].lastMessageTime
          };

          // Re-sort conversations by lastMessageTime
          sortConversationIds(state);
        }
      }
    },

    // ADD NEW MESSAGE FROM REAL-TIME LISTENER
    addMessageRealtime(state, action) {
      const { conversationId, message } = action.payload;

      if (conversationId && state.conversations.byId[conversationId]) {
        // Check if message already exists
        const existingMessage = state.conversations.byId[conversationId].messages.find(
          m => m.id === message.id
        );
        if (!existingMessage) {
          state.conversations.byId[conversationId].messages.push(message);

          // Update lastMessageTime
          state.conversations.byId[conversationId].lastMessageTime =
            message.createdAt ? new Date(message.createdAt).getTime() : Date.now();
          state.conversations.byId[conversationId].lastMessage = {
            content: message.body,
            type: message.contentType,
            senderId: message.senderId,
            timestamp: state.conversations.byId[conversationId].lastMessageTime
          };

          // Re-sort conversations by lastMessageTime
          sortConversationIds(state);
        }
      }
    },

    // MARK THE CONVERSATION AS SEEN
    markAsSeenSuccess(state, action) {
      const { conversationId } = action.payload;
      const conversation = state.conversations.byId[conversationId];

      if (conversation) {
        conversation.unreadCount = 0;
      }
    },

    // SET ACTIVE CONVERSATION (instant switch, no data fetch)
    setActiveConversation(state, action) {
      const conversationId = action.payload;
      if (conversationId) {
        state.currentConversationId = String(conversationId);
        state.recipients = [];
      }
    },

    // RESET ACTIVE CONVERSATION
    resetActiveConversation(state) {
      state.currentConversationId = null;
    },

    // ADD RECIPIENTS WHEN CREATE NEW CONVERSATION
    addRecipients(state, action) {
      state.recipients = action.payload;
    },

    // TOGGLE A REACTION ON A MESSAGE
    // payload: { conversationId, messageId, userId, emoji }
    // - If the user already reacted with this emoji, remove it (toggle off).
    // - If the user already reacted with a different emoji, replace it.
    // - Otherwise append.
    setMessageReaction(state, action) {
      const { conversationId, messageId, userId, emoji } = action.payload;
      const conv = state.conversations.byId[conversationId]
        || state.conversations.byId[String(conversationId)];
      if (!conv) return;
      const msg = conv.messages.find((m) => String(m.id) === String(messageId));
      if (!msg) return;
      const existing = Array.isArray(msg.reactions) ? msg.reactions : [];
      const sameUserIdx = existing.findIndex((r) => String(r.userId) === String(userId));
      let next;
      if (sameUserIdx === -1) {
        next = [...existing, { userId: String(userId), emoji }];
      } else if (existing[sameUserIdx].emoji === emoji) {
        next = existing.filter((_, i) => i !== sameUserIdx);
      } else {
        next = existing.map((r, i) => (i === sameUserIdx ? { ...r, emoji } : r));
      }
      msg.reactions = next;
      // Cache so reactions survive a page refresh even if the server response
      // is delayed or fails — replaceMessageReactions overwrites this when the
      // server confirms.
      setReactionsFor(messageId, next);
    },

    // Update participant online/offline status across a conversation.
    // payload: { conversationId, presenceMap: { [userId]: { online, lastSeenAt } } }
    setParticipantPresence(state, action) {
      const { conversationId, presenceMap } = action.payload || {};
      if (!conversationId || !presenceMap) return;
      const conv = state.conversations.byId[conversationId]
        || state.conversations.byId[String(conversationId)];
      if (!conv || !Array.isArray(conv.participants)) return;
      conv.participants = conv.participants.map((p) => {
        const pres = presenceMap[String(p.id)];
        if (!pres) return p;
        const lastSeen = pres.lastSeenAt ?? pres.lastSeen;
        return {
          ...p,
          status: pres.online ? 'online' : 'offline',
          lastActivity: lastSeen ? new Date(lastSeen).toISOString() : null,
        };
      });
    },

    setUsersPresence(state, action) {
      const presenceMap = action.payload || {};
      Object.values(state.conversations.byId).forEach((conv) => {
        if (!Array.isArray(conv.participants)) return;
        conv.participants = conv.participants.map((p) => {
          const pres = presenceMap[String(p.id)];
          if (!pres) return p;
          const lastSeen = pres.lastSeenAt ?? pres.lastSeen;
          return {
            ...p,
            status: pres.online ? 'online' : 'offline',
            lastActivity: lastSeen ? new Date(lastSeen).toISOString() : null,
          };
        });
      });
    },

    // Replace a message's reactions with the server-authoritative array.
    replaceMessageReactions(state, action) {
      const { conversationId, messageId, reactions } = action.payload;
      const conv = state.conversations.byId[conversationId]
        || state.conversations.byId[String(conversationId)];
      if (!conv) return;
      const msg = conv.messages.find((m) => String(m.id) === String(messageId));
      if (!msg) return;
      msg.reactions = Array.isArray(reactions) ? reactions : [];
      setReactionsFor(messageId, msg.reactions);
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { addRecipients, resetActiveConversation, setActiveConversation, setMessageReaction, setParticipantPresence } = slice.actions;

// ----------------------------------------------------------------------

// getContacts is now handled by getConversations to avoid duplicate API calls.
// This function is kept for backward compatibility but is a no-op.
export function getContacts() {
  return async () => {
    // Contacts are now dispatched from getConversations using the same API response
  };
}

// ----------------------------------------------------------------------

export function getConversations(userId) {
  return async (dispatch, getState) => {
    if (!userId) {
      dispatch(slice.actions.getConversationsSuccess([]));
      return;
    }

    const existingState = getState();
    const hasExistingData = existingState.chat.conversations.allIds.length > 0;
    if (!hasExistingData) {
      dispatch(slice.actions.getConversationsStart());
    }

    // Tear down any previous chat-list listener before creating a new one
    if (activeChatListListener) {
      activeChatListListener();
      activeChatListListener = null;
    }

    try {
      const currentUserId = String(userId);
      const s3Base = process.env.REACT_APP_S3_BUCKET || '';

      const mapUser = (u) => ({
        id: u.id,
        name: u.name || 'Unknown',
        avatarUrl: u.avatar ? `${s3Base}/${u.avatar}` : null,
        status: 'offline',
        lastActivity: null,
        role: '',
        address: '',
        phoneNumber: '',
        email: '',
      });

      const chatsQuery = query(
        collection(db, `${FIREBASE_PREFIX}_chats`),
        where('participants', 'array-contains', currentUserId)
      );

      activeChatListListener = onSnapshot(
        chatsQuery,
        (snapshot) => {
          const conversations = snapshot.docs.map((docSnap) => {
            const chat = docSnap.data();
            const chatId = docSnap.id;

            const lastMessageTime = chat.updatedAt?.toMillis
              ? chat.updatedAt.toMillis()
              : chat.createdAt?.toMillis
              ? chat.createdAt.toMillis()
              : null;

            const unreadCount = chat.unreadCounts?.[currentUserId] || 0;

            if (chat.isGroup) {
              return {
                id: chat.userIds,
                chatId,
                messages: [],
                participants: (chat.users || []).map(mapUser),
                unreadCount,
                type: 'GROUP',
                isProposal: !!chat.isProposal,
                proposalId: chat.proposalId || null,
                groupName: chat.groupName || '',
                lastMessage: sanitizeLastMessage(chat.lastMessage),
                lastMessageTime,
              };
            }

            // 1-to-1: the conversation id is the other participant's id
            const otherUserId = (chat.participants || []).find((pid) => pid !== currentUserId);
            const otherUser = (chat.users || []).find((u) => String(u.id) === String(otherUserId));

            return {
              id: String(otherUserId || chatId),
              chatId,
              messages: [],
              participants: otherUser ? [mapUser(otherUser)] : [],
              unreadCount,
              type: 'ONE_TO_ONE',
              lastMessage: sanitizeLastMessage(chat.lastMessage),
              lastMessageTime,
            };
          });

          dispatch(slice.actions.getConversationsSuccess(conversations));

          const presenceUserIds = [
            ...new Set(
              conversations
                .flatMap((conversation) => conversation.participants || [])
                .map((participant) => participant?.id)
                .filter((id) => id != null && String(id) !== currentUserId)
                .map(String)
            ),
          ];

          if (activeChatPresenceListener) {
            activeChatPresenceListener();
            activeChatPresenceListener = null;
          }

          if (presenceUserIds.length > 0) {
            activeChatPresenceListener = subscribeToPresence(presenceUserIds, (presenceMap) => {
              dispatch(slice.actions.setUsersPresence(presenceMap));
            });
          }

          const contacts = conversations
            .filter((c) => c.type === 'ONE_TO_ONE')
            .map((c) => ({
              id: c.id,
              name: c.participants[0]?.name || 'Unknown User',
              avatarUrl: c.participants[0]?.avatarUrl || null,
              lastMessage: c.lastMessage?.content || '',
              lastMessageAt: c.lastMessageTime,
              unreadCount: c.unreadCount || 0,
            }));
          dispatch(slice.actions.getContactsSuccess(contacts));
        },
        (error) => {
          console.error('Error in chat list listener:', error);
          dispatch(slice.actions.getConversationsFailure(error.message));
        }
      );
    } catch (error) {
      console.error('Error setting up chat list listener:', error);
      dispatch(slice.actions.getConversationsFailure(error.message));
    }
  };
}

export function unsubscribeFromConversations() {
  return () => {
    if (activeChatListListener) {
      activeChatListListener();
      activeChatListListener = null;
    }
    if (activeChatPresenceListener) {
      activeChatPresenceListener();
      activeChatPresenceListener = null;
    }
  };
}

// ----------------------------------------------------------------------

export function getGroupConversation(projectId) {
  return async (dispatch, getState) => {
    try {
      const response = await axios.get(API_ENDPOINTS.messages.groupConversation(projectId));

      if (response.data.success) {
        const data = response.data;
        const conversation = {
          id: `group_project_${projectId}`,
          chatId: data.chatId,
          messages: (data.data || []).map(msg => ({
            id: msg.id || `msg-${Date.now()}-${Math.random()}`,
            body: msg.content || '',
            contentType: msg.messageType || 'text',
            attachments: [],
            createdAt: msg.createdAt || new Date().toISOString(),
            senderId: msg.senderId,
            receiverId: msg.receiverId,
            replyTo: msg.replyTo || null,
            reactions: normalizeReactionsPayload(msg.reactions),
          })),
          participants: (data.users || []).map(u => ({
            id: u.id,
            name: u.name || 'Unknown',
            avatarUrl: u.avatarPath
              ? `${process.env.REACT_APP_S3_BUCKET}/${u.avatarPath}`
              : null,
            status: 'offline',
            lastActivity: null,
            role: u.userType || '',
            address: u.address || '',
            phoneNumber: u.phone || '',
            email: u.email || '',
          })),
          unreadCount: 0,
          type: 'GROUP',
          groupName: data.groupName,
          projectId: parseInt(projectId, 10),
          ownerId: data.ownerId ?? data.createdBy ?? data.owner_id ?? null,
        };
        dispatch(slice.actions.getConversationSuccess(conversation));
      }
    } catch (error) {
      console.error('Error fetching group conversation:', error);
      // On error, use existing conversation data from getConversations if available
      const conversationId = `group_project_${projectId}`;
      const state = getState();
      const existing = state?.chat?.conversations?.byId?.[conversationId];

      if (existing) {
        // Just set it as active without overwriting
        dispatch(slice.actions.getConversationSuccess(existing));
      } else {
        dispatch(slice.actions.getConversationSuccess({
          id: conversationId,
          messages: [],
          participants: [],
          unreadCount: 0,
          type: 'GROUP',
          groupName: 'Project Group',
          projectId: parseInt(projectId, 10),
        }));
      }
    }
  };
}

export function getProposalConversation(folderId) {
  return async (dispatch, getState) => {
    try {
      const response = await axios.get(API_ENDPOINTS.proposals.chatConversation(folderId));

      if (response.data.success) {
        const data = response.data;
        const s3Base = process.env.REACT_APP_S3_BUCKET || '';
        const toAttachmentUrl = (a) => {
          if (!a) return null;
          if (typeof a === 'string') {
            return a.startsWith('http') ? a : `${s3Base}/${a}`;
          }
          const key = a.url || a.preview || '';
          if (!key) return null;
          return key.startsWith('http') ? key : `${s3Base}/${key}`;
        };
        const conversation = {
          id: `proposal_${folderId}`,
          chatId: data.chatId,
          messages: (data.data || []).map(msg => ({
            id: msg.id || `pd-${Date.now()}-${Math.random()}`,
            body: msg.content || '',
            contentType: msg.messageType || 'text',
            attachments: (Array.isArray(msg.attachments) ? msg.attachments : [])
              .map(toAttachmentUrl)
              .filter(Boolean),
            createdAt: msg.createdAt || new Date().toISOString(),
            senderId: msg.senderId,
            receiverId: msg.receiverId || 'proposal',
            replyTo: msg.replyTo || null,
            reactions: normalizeReactionsPayload(msg.reactions),
          })),
          participants: (data.users || []).map(u => ({
            id: u.id,
            name: u.name || 'Unknown',
            avatarUrl: u.avatarPath
              ? `${process.env.REACT_APP_S3_BUCKET}/${u.avatarPath}`
              : null,
            status: 'offline',
            lastActivity: null,
            role: u.userType || '',
            address: u.address || '',
            phoneNumber: u.phone || '',
            email: u.email || '',
          })),
          unreadCount: 0,
          type: 'GROUP',
          isProposal: true,
          proposalId: parseInt(folderId, 10),
          groupName: data.groupName,
          ownerId: data.ownerId ?? data.createdBy ?? data.owner_id ?? null,
        };
        dispatch(slice.actions.getConversationSuccess(conversation));
      }
    } catch (error) {
      console.error('Error fetching proposal conversation:', error);
      const conversationId = `proposal_${folderId}`;
      const state = getState();
      const existing = state?.chat?.conversations?.byId?.[conversationId];
      if (existing) {
        dispatch(slice.actions.getConversationSuccess(existing));
      } else {
        dispatch(slice.actions.getConversationSuccess({
          id: conversationId,
          messages: [],
          participants: [],
          unreadCount: 0,
          type: 'GROUP',
          isProposal: true,
          proposalId: parseInt(folderId, 10),
          groupName: 'Proposal',
        }));
      }
    }
  };
}

export function getConversation(otherUserId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getConversationStart());
      // Handle proposal conversations
      if (String(otherUserId).startsWith('proposal_')) {
        const folderId = String(otherUserId).replace('proposal_', '');
        return await dispatch(getProposalConversation(folderId));
      }

      // Handle group conversations
      if (String(otherUserId).startsWith('group_project_')) {
        const projectId = String(otherUserId).replace('group_project_', '');
        return await dispatch(getGroupConversation(projectId));
      }

      // Get user information for participants first (we need this regardless of messages)
      let participants = [];
      try {
        const userListResponse = await axios.get(API_ENDPOINTS.messages.userList, {
          params: { userIds: [otherUserId].join(',') },
        });

        if (userListResponse.data.success && userListResponse.data.data && userListResponse.data.data.length > 0) {
          const userInfo = userListResponse.data.data[0];
          if (userInfo) {
            participants = [{
              id: userInfo.id,
              name: userInfo.name || 'Unknown User',
              avatarUrl: userInfo.avatarPath
                ? `${process.env.REACT_APP_S3_BUCKET}/${userInfo.avatarPath}`
                : null,
              status: 'offline',
              lastActivity: null,
              role: userInfo.userType || '', // User type (job_seeker, agency, organization)
              address: userInfo.address || '',
              phoneNumber: userInfo.phoneNumber || '',
              email: userInfo.email || '',
              bio: userInfo.bio || '',
            }];
          }
        }
      } catch (userError) {
        console.error('Error fetching user info:', userError);
      }
      
      // Ensure participants array is not empty (fallback)
      if (participants.length === 0) {
        participants = [{
          id: otherUserId,
          name: 'User',
          avatarUrl: null,
          status: 'offline',
          lastActivity: null,
          role: '',
          address: '',
          phoneNumber: '',
          email: '',
        }];
      }
      
      // Get message history with the other user
      let messages = [];
      let chatId = null;
      try {
        const response = await axios.get(API_ENDPOINTS.messages.history(otherUserId));

        if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
          messages = response.data.data;
        }
        // Get chatId from response
        chatId = response.data.chatId || null;
      } catch (messageError) {
        console.error('Error fetching message history:', messageError);
        // Continue even if message history fails - we can still create a conversation
      }

      // Transform messages to conversation format
      // Ensure otherUserId is a string for consistency
      const conversationId = String(otherUserId);

      const conversation = {
        id: conversationId, // Use otherUserId as conversation ID (ensure it's a string)
        chatId, // Firebase document ID for the chat
        messages: messages.map((msg) => ({
          id: msg.id || msg.messageId || `msg-${Date.now()}-${Math.random()}`,
          body: msg.content || '',
          contentType: msg.messageType || 'text',
          attachments: msg.mediaUrl ? [msg.mediaUrl] : [],
          createdAt: msg.createdAt || msg.timestamp || new Date().toISOString(),
          senderId: msg.senderId,
          receiverId: msg.receiverId,
          replyTo: msg.replyTo || null,
          reactions: normalizeReactionsPayload(msg.reactions),
        })),
        participants,
        unreadCount: 0,
        type: 'ONE_TO_ONE',
      };

      dispatch(slice.actions.getConversationSuccess(conversation));
    } catch (error) {
      console.error('Error fetching conversation:', error);
      // Even on error, create a basic conversation so the UI can still work
      const conversationId = String(otherUserId);
      const fallbackConversation = {
        id: conversationId,
        messages: [],
        participants: [{
          id: conversationId,
          name: 'User',
          avatarUrl: null,
          status: 'offline',
          lastActivity: null,
          role: '',
          address: '',
          phoneNumber: '',
          email: '',
        }],
        unreadCount: 0,
        type: 'ONE_TO_ONE',
      };
      dispatch(slice.actions.getConversationSuccess(fallbackConversation));
    }
  };
}

// ----------------------------------------------------------------------

export function sendMessage(receiverId, body, messageType = 'text', mediaUrl = null, options = {}) {
  return async (dispatch, getState) => {
    try {
      // Get current user ID first for optimistic update
      let senderId = null;
      try {
        const userResponse = await axios.get(API_ENDPOINTS.auth.me);
        senderId = userResponse.data.user?.id;
      } catch (userError) {
        console.error('Error getting current user:', userError);
      }

      // Get chatId from the existing conversation if available
      const state = getState();
      const conversation = state.chat.conversations.byId[receiverId] ||
                          state.chat.conversations.byId[String(receiverId)];
      const chatId = conversation?.chatId || null;

      const isProposal = String(receiverId).startsWith('proposal_');
      const isGroup = String(receiverId).startsWith('group_project_');
      const replyTo = options?.replyTo || null;

      // Optimistic UI: only for 1-to-1 chats. Proposal/group chats are mirrored
      // through Firebase, and the snapshot listener appends the real message
      // shortly after — adding an optimistic copy here causes duplicates that
      // only clear on a fresh fetch.
      if (!isProposal && !isGroup) {
        const optimisticMessage = {
          id: `temp-${Date.now()}`,
          body: body,
          contentType: messageType,
          attachments: mediaUrl ? [mediaUrl] : [],
          createdAt: new Date().toISOString(),
          senderId: String(senderId),
          receiverId: String(receiverId),
          replyTo,
        };

        dispatch(slice.actions.sendMessageSuccess({
          conversationId: receiverId,
          message: optimisticMessage,
        }));
      }

      // Handle proposal chat messages
      if (isProposal) {
        const folderId = String(receiverId).replace('proposal_', '');
        const payload = { content: body };
        if (replyTo) {
          payload.replyTo = replyTo;
        }
        if (mediaUrl) {
          const mu = typeof mediaUrl === 'object' ? mediaUrl : { url: mediaUrl };
          payload.mediaUrl = mu.url || mu.key || mediaUrl;
          payload.originalName = mu.originalName || '';
          payload.mimeType = mu.mimeType || (messageType === 'image' ? 'image/jpeg' : '');
          payload.size = mu.size || 0;
        }
        await axios.post(API_ENDPOINTS.proposals.chatSend(folderId), payload);
        // Firebase listener will pick up the mirrored message
        return;
      }

      // Handle group messages
      if (isGroup) {
        if (!chatId) {
          console.error('Group chat not initialized, no chatId');
          return;
        }
        await axios.post(API_ENDPOINTS.messages.groupSend, {
          chatId,
          content: body,
          messageType,
          replyTo,
        });
        // Existing Firebase snapshot listener will pick up the new message automatically
        return;
      }

      // 1-to-1 message
      const data = {
        receiverId,
        content: body,
        messageType,
        mediaUrl,
        chatId,
        replyTo,
      };

      const response = await axios.post(API_ENDPOINTS.messages.send, data);

      if (response.data.success) {
        if (senderId) {
          setTimeout(() => {
            dispatch(subscribeToMessages(senderId, receiverId));
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };
}

export function createNewConversation(recipients, body, messageType = 'text', mediaUrl = null) {
  return async (dispatch) => {
    try {
      // For new conversation, send message to the first recipient
      // The backend will handle creating the conversation
      if (recipients && recipients.length > 0) {
        const receiverId = recipients[0].id || recipients[0];
        await dispatch(sendMessage(receiverId, body, messageType, mediaUrl));
        
        // After sending, fetch the conversation
        await dispatch(getConversation(receiverId));
      }
    } catch (error) {
      console.error('Error creating new conversation:', error);
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function markAsSeen(senderId) {
  return async (dispatch) => {
    try {
      // Ensure senderId is a valid string or number, not an object
      const validSenderId = typeof senderId === 'object'
        ? (senderId.id || String(senderId))
        : senderId;

      if (!validSenderId) {
        console.error('Invalid senderId provided to markAsSeen:', senderId);
        return;
      }

      await axios.patch(API_ENDPOINTS.messages.markAsRead(validSenderId));

      dispatch(slice.actions.markAsSeenSuccess({ conversationId: validSenderId }));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function searchUsers(searchQuery) {
  return async (dispatch) => {
    try {
      const response = await axios.get(API_ENDPOINTS.messages.searchUsers, {
        params: { search: searchQuery }
      });

      if (response.data.success && response.data.data) {
        // Transform the user data to match the contacts format
        const users = response.data.data.map(user => ({
          id: user.id,
          name: user.name || user.displayName || user.email || 'Unknown User',
          email: user.email,
          avatarUrl: user.avatarPath
            ? `${process.env.REACT_APP_S3_BUCKET}/${user.avatarPath}`
            : null,
          status: user.isOnline ? 'online' : 'offline',
          lastActivity: user.lastSeen || null,
          role: user.role || '',
        }));

        dispatch(slice.actions.searchUsersSuccess(users));
        return users;
      }

      dispatch(slice.actions.searchUsersSuccess([]));
      return [];
    } catch (error) {
      console.error('Error searching users:', error);
      dispatch(slice.actions.searchUsersSuccess([]));
      return [];
    }
  };
}

// ----------------------------------------------------------------------

// Subscribe to real-time messages for a conversation
export function subscribeToMessages(currentUserId, otherUserId) {
  return async (dispatch, getState) => {
    try {
      // Unsubscribe from any existing listener
      if (activeMessageListener) {
        activeMessageListener();
        activeMessageListener = null;
      }

      if (!currentUserId || !otherUserId) {
        return null;
      }

      let userIds;

      // Handle proposal chat subscriptions (same userIds pattern as groups)
      if (String(otherUserId).startsWith('proposal_')) {
        userIds = String(otherUserId);
      } else if (String(otherUserId).startsWith('group_project_')) {
        // Handle group chat subscriptions
        userIds = String(otherUserId);
      } else {
        // Sort numerically to match server behavior for 1-to-1
        const id1 = parseInt(currentUserId, 10);
        const id2 = parseInt(otherUserId, 10);
        const sortedIds = id1 < id2 ? [id1, id2] : [id2, id1];
        userIds = sortedIds.map(id => id.toString()).join("_");
      }

      // Find the chat document
      const chatQuery = query(
        collection(db, `${FIREBASE_PREFIX}_chats`),
        where("userIds", "==", userIds)
      );

      const chatSnapshot = await getDocs(chatQuery);

      if (chatSnapshot.empty) {
        return null;
      }

      const chatId = chatSnapshot.docs[0].id;

      // Subscribe to messages in this chat
      const messagesQuery = query(
        collection(db, `${FIREBASE_PREFIX}_messages`),
        where("chatId", "==", chatId),
        orderBy("timestamp", "asc")
      );

      const isProposalChat = String(otherUserId).startsWith('proposal_');

      activeMessageListener = onSnapshot(messagesQuery, (snapshot) => {
        const conversationId = String(otherUserId);

        if (isProposalChat) {
          // SQL is the source of truth for proposal history; Firebase carries live deltas.
          // Append each new message individually; addMessageRealtime dedups by id.
          snapshot.docChanges().forEach((change) => {
            if (change.type !== 'added') return;
            const data = change.doc.data();
            // Use SQL-stable id so it matches the SQL history already loaded
            const stableId = data.proposalDiscussionId
              ? `pd_${data.proposalDiscussionId}`
              : change.doc.id;
            const message = {
              id: stableId,
              body: data.content || '',
              contentType: data.type || 'text',
              attachments: data.mediaUrl ? [data.mediaUrl] : [],
              createdAt: data.timestamp?.toMillis
                ? new Date(data.timestamp.toMillis()).toISOString()
                : new Date().toISOString(),
              senderId: data.senderId,
              receiverId: data.receiverId,
              replyTo: data.replyTo || null,
              reactions: normalizeReactionsPayload(data.reactions),
            };
            const state = getState();
            if (state.chat.conversations.byId[conversationId]) {
              dispatch(slice.actions.addMessageRealtime({ conversationId, message }));
            }
          });
          return;
        }

        const messages = snapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          const stableId = data.proposalDiscussionId
            ? `pd_${data.proposalDiscussionId}`
            : docSnapshot.id;
          return {
            id: stableId,
            body: data.content || '',
            contentType: data.type || 'text',
            attachments: data.mediaUrl ? [data.mediaUrl] : [],
            createdAt: data.timestamp?.toMillis
              ? new Date(data.timestamp.toMillis()).toISOString()
              : new Date().toISOString(),
            senderId: data.senderId,
            receiverId: data.receiverId,
            replyTo: data.replyTo || null,
            reactions: normalizeReactionsPayload(data.reactions),
          };
        });

        const state = getState();
        if (state.chat.conversations.byId[conversationId]) {
          dispatch(slice.actions.updateMessagesRealtime({
            conversationId,
            messages
          }));
        }
        // If conversation is not in store yet, skip — getConversation will load it
      }, (error) => {
        console.error('Error in real-time message listener:', error);
      });

      return activeMessageListener;
    } catch (error) {
      console.error('Error subscribing to messages:', error);
      return null;
    }
  };
}

// Unsubscribe from real-time messages
export function unsubscribeFromMessages() {
  return () => {
    if (activeMessageListener) {
      activeMessageListener();
      activeMessageListener = null;
    }
  };
}

// ----------------------------------------------------------------------

// Convert the server's `reactions` payload into our canonical
// [{ userId, emoji }] shape. The server is documented to return an object;
// this handles the common shapes defensively:
//   - { "👍": ["uid1","uid2"], "❤️": ["uid3"] }   emoji → user ids
//   - { "uid1": "👍", "uid2": "❤️" }              user id → emoji
//   - [ { userId, emoji }, ... ]                   already-normalized array
function normalizeReactionsPayload(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .filter((r) => r && r.userId != null && r.emoji)
      .map((r) => ({ userId: String(r.userId), emoji: r.emoji }));
  }
  if (typeof raw !== 'object') return [];
  const entries = Object.entries(raw);
  if (entries.length === 0) return [];
  const firstValue = entries[0][1];
  // Pattern A: emoji → array of user ids
  if (Array.isArray(firstValue)) {
    const out = [];
    entries.forEach(([emoji, userIds]) => {
      (Array.isArray(userIds) ? userIds : []).forEach((uid) => {
        if (uid != null) out.push({ userId: String(uid), emoji });
      });
    });
    return out;
  }
  // Pattern B: user id → emoji
  return entries
    .filter(([, e]) => typeof e === 'string' && e)
    .map(([uid, e]) => ({ userId: String(uid), emoji: e }));
}

// React to a message. Updates Redux + localStorage optimistically, then POSTs
// to /api/messages/:messageId/reactions. The server response — which carries
// the canonical reactions object — replaces the optimistic value on success.
export function reactToMessage(messageId, emoji, userId) {
  return async (dispatch, getState) => {
    if (!emoji || messageId == null || userId == null) return;
    const state = getState();
    const conversationId = state.chat.currentConversationId;
    if (!conversationId) return;

    dispatch(slice.actions.setMessageReaction({
      conversationId,
      messageId,
      userId: String(userId),
      emoji,
    }));

    // Proposal-discussion ids are prefixed `pd_<sqlId>` in the local store; the
    // backend keeps the bare numeric id.
    const rawId = String(messageId);
    const apiMessageId = rawId.startsWith('pd_') ? rawId.slice(3) : rawId;

    try {
      const res = await axios.post(
        API_ENDPOINTS.messages.reactions(apiMessageId),
        { emoji }
      );
      if (res.data?.success) {
        const normalized = normalizeReactionsPayload(res.data.data?.reactions);
        dispatch(slice.actions.replaceMessageReactions({
          conversationId,
          messageId,
          reactions: normalized,
        }));
      }
    } catch (err) {
      console.warn('Failed to persist reaction:', err?.message || err);
      // Optimistic value remains; localStorage cache keeps it across refreshes
      // until the server is reachable again.
    }
  };
}
